/**
 * Map ZITADEL claims onto the display-string role contract the rest of
 * the SPA already uses (`MISSION FACILITATOR`, `DATA ANALYST`, etc.).
 *
 * ZITADEL emits project roles under the configured custom claim. With
 * "User roles inside ID Token" enabled on the application (Console →
 * Application → Token Settings) the claim is:
 *
 *   "urn:zitadel:iam:org:project:roles": {
 *     "MISSION_FACILITATOR": { "<orgId>": "<orgDomain>" },
 *     "DATA_ANALYST":        { ... }
 *   }
 *
 * The keys (uppercased, underscore-separated) are the role *names* the
 * facilitator types into the Console when defining roles for the project.
 * We map underscore → space + a few legacy aliases to keep the existing
 * `user.role === "MISSION FACILITATOR"` consumers untouched.
 */

import type { User } from "oidc-client-ts";

export const ROLE_CLAIM = "urn:zitadel:iam:org:project:roles";

/** Display strings the rest of the SPA already compares against. */
export const ROLE_FACILITATOR = "MISSION FACILITATOR";
export const ROLE_ANALYST = "DATA ANALYST";
export const ROLE_OBSERVER = "MISSION OBSERVER";
export const ROLE_DIRECTOR = "MISSION DIRECTOR";
export const ROLE_ENGINEER = "SYSTEMS ENGINEER";

const DEFAULT_ROLE = ROLE_OBSERVER;

const ROLE_KEY_TO_DISPLAY: Record<string, string> = {
  MISSION_FACILITATOR: ROLE_FACILITATOR,
  DATA_ANALYST: ROLE_ANALYST,
  MISSION_OBSERVER: ROLE_OBSERVER,
  MISSION_DIRECTOR: ROLE_DIRECTOR,
  SYSTEMS_ENGINEER: ROLE_ENGINEER,
};

/**
 * Inverse of `ROLE_KEY_TO_DISPLAY` — used by Manage Crew when writing a
 * role change back to ZITADEL. The UI talks in display strings; ZITADEL
 * takes the underscore-form key.
 */
const ROLE_DISPLAY_TO_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(ROLE_KEY_TO_DISPLAY).map(([k, v]) => [v, k]),
);

export function roleKeyFromDisplay(display: string): string | null {
  return ROLE_DISPLAY_TO_KEY[display] ?? null;
}

export function roleDisplayFromKey(key: string): string | null {
  return ROLE_KEY_TO_DISPLAY[key] ?? null;
}

/**
 * Priority order when a user has multiple roles — most-privileged wins so
 * the HudHeader pill + facilitator gates pick the right one.
 */
const ROLE_PRIORITY = [
  ROLE_FACILITATOR,
  ROLE_DIRECTOR,
  ROLE_ANALYST,
  ROLE_ENGINEER,
  ROLE_OBSERVER,
];

export function roleFromClaims(user: User | null): string {
  if (!user) return DEFAULT_ROLE;
  const claims = user.profile as Record<string, unknown>;
  const rolesClaim = claims[ROLE_CLAIM];
  if (!rolesClaim || typeof rolesClaim !== "object") return DEFAULT_ROLE;

  const matched: string[] = [];
  for (const key of Object.keys(rolesClaim as Record<string, unknown>)) {
    const display = ROLE_KEY_TO_DISPLAY[key];
    if (display) matched.push(display);
  }
  if (matched.length === 0) return DEFAULT_ROLE;

  for (const role of ROLE_PRIORITY) {
    if (matched.includes(role)) return role;
  }
  return matched[0] ?? DEFAULT_ROLE;
}

/**
 * The user's resident-org id (multi-client plan) — the client identity
 * anchor. Prefers the reserved-scope resourceowner claim; falls back to the
 * first org id inside the role-claim values (grant resource owners) purely
 * for cache-keying continuity when the reserved scope isn't emitted. The
 * BFF's server-side resolution is the enforcement path, not this.
 */
export const RESOURCEOWNER_CLAIM = "urn:zitadel:iam:user:resourceowner:id";

export function clientOrgIdFromClaims(user: User | null): string | null {
  if (!user) return null;
  const claims = (user.profile ?? {}) as Record<string, unknown>;
  const resident = claims[RESOURCEOWNER_CLAIM];
  if (typeof resident === "string" && resident.trim()) return resident.trim();
  const rolesClaim = claims[ROLE_CLAIM];
  if (rolesClaim && typeof rolesClaim === "object") {
    for (const grant of Object.values(rolesClaim as Record<string, unknown>)) {
      if (grant && typeof grant === "object") {
        const orgId = Object.keys(grant as Record<string, unknown>)[0];
        if (orgId) return orgId;
      }
    }
  }
  return null;
}

/**
 * Best-effort display name pulled from standard OIDC profile claims.
 * Falls back through `name` → `given_name + family_name` → `preferred_username`
 * → `email` → sub, so something always renders in the HudHeader.
 */
export function displayNameFromClaims(user: User | null): string {
  if (!user) return "Operator";
  const p = user.profile;
  if (p.name) return p.name;
  const given = p.given_name ?? "";
  const family = p.family_name ?? "";
  const composed = `${given} ${family}`.trim();
  if (composed) return composed;
  if (p.preferred_username) return p.preferred_username;
  if (p.email) return p.email;
  return p.sub ?? "Operator";
}

/**
 * Username slug for the HudHeader's "@handle" line and the Manage-Crew row.
 * Prefer `preferred_username`, fall back to the email local-part, then sub.
 */
export function usernameFromClaims(user: User | null): string {
  if (!user) return "operator";
  const p = user.profile;
  if (p.preferred_username) return p.preferred_username;
  if (p.email) return p.email.split("@")[0] ?? p.email;
  return p.sub ?? "operator";
}
